import React, { useState, useEffect } from "react";
import { styled, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from "@mui/material";
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  PeopleAlt as PeopleAltIcon,
  LockOutlined as LockOutlinedIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import {
  blue,
  green,
  amber,
  indigo,
  teal,
  red,
} from "@mui/material/colors";
import AdminLayout from "./AdminLayout";
import useAdminContext from "../../store/contexts/AdminContext";
import FormatDateDurationForMessagesDate from "../../utils/FormatDateDurationForMessagesDate";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";
import { useHistory } from "react-router-dom";

const StatsCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  border: "1px solid #e3f2fd",
  borderRadius: "16px",
  transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 24px rgba(33, 150, 243, 0.15)",
    border: "1px solid #2196f3",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// Helper functions for formatting actions
const formatActionText = (action) => {
  if (!action) return "Unknown";
  return action
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getActionColor = (action) => {
  if (!action) return "default";
  const actionLower = action.toLowerCase();

  if (actionLower.includes("login")) return "info";
  if (actionLower.includes("create")) return "success";
  if (actionLower.includes("suspend") || actionLower.includes("forgot"))
    return "warning";
  if (actionLower.includes("reset") || actionLower.includes("delete"))
    return "error";
  if (actionLower.includes("activate") || actionLower.includes("verify"))
    return "success";
  if (actionLower.includes("update") || actionLower.includes("edit"))
    return "primary";

  return "default";
};

const StatCardComponent = ({ title, value, icon, bgColor }) => (
  <StatsCard elevation={3}>
    <CardContent>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Avatar sx={{ bgcolor: bgColor }}>{icon}</Avatar>
      </Box>
      <Typography variant="h4" component="div">
        {typeof value === "number" ? value.toLocaleString() : value}
      </Typography>
    </CardContent>
  </StatsCard>
);

const Dashboard = () => {
  const history = useHistory();
  const { getDashboardOverviewApiCall } = useAdminContext();
  const [dashboardData, setDashboardData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const dashboardOverview = await getDashboardOverviewApiCall();
      if (dashboardOverview) {
        setDashboardData(dashboardOverview);
        setRecentActivity(dashboardOverview.recent_activity || []);
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !dashboardData) {
    return (
      <AdminLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <LinearProgress sx={{ width: "100%" }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: "#1a1a1a" }}>
          Dashboard Overview
        </Typography>

        {/* Stats Cards - First Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="Total Admins"
              value={dashboardData.admins?.total || 0}
              icon={<AdminPanelSettingsIcon />}
              bgColor={blue[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="Active Users"
              value={dashboardData.users?.total || 0}
              icon={<PeopleAltIcon />}
              bgColor={green[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="Suspended Accounts"
              value={dashboardData.users?.suspended || 0}
              icon={<LockOutlinedIcon />}
              bgColor={amber[700]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="Activity Today"
              value={dashboardData.system?.activity_today || 0}
              icon={<TimelineIcon />}
              bgColor={indigo[500]}
            />
          </Grid>
        </Grid>

        {/* Stats Cards - Second Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="Total Revenue"
              value={`$${(
                dashboardData.payments?.total_revenue || 0
              ).toLocaleString()}`}
              icon={<BarChartIcon />}
              bgColor={teal[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="This Month"
              value={`$${(
                dashboardData.payments?.this_month || 0
              ).toLocaleString()}`}
              icon={<BarChartIcon />}
              bgColor={green[600]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="Total Rooms"
              value={dashboardData.rooms?.total || 0}
              icon={<DescriptionIcon />}
              bgColor={red[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCardComponent
              title="Active Sessions"
              value={dashboardData.system?.active_sessions || 0}
              icon={<AccessTimeIcon />}
              bgColor={amber[600]}
            />
          </Grid>
        </Grid>

        {/* Recent Activity and System Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{ p: 2, height: "100%", overflow: "hidden" }}
              elevation={3}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Recent Activity</Typography>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => history.push(ADMIN_ROUTES.logs)}
                >
                  View All
                </Button>
              </Box>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Admin</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentActivity.length > 0 ? (
                      recentActivity.slice(0, 5).map((activity, index) => (
                        <StyledTableRow key={activity.id || index}>
                          <TableCell>
                            {activity.admin || activity.admin_email || "System"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatActionText(
                                activity.action || activity.description,
                              )}
                              size="small"
                              color={getActionColor(
                                activity.action || activity.description,
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            {activity.timestamp
                              ? FormatDateDurationForMessagesDate(
                                  activity.timestamp,
                                  true,
                                )
                              : activity.created_at
                                ? FormatDateDurationForMessagesDate(
                                    activity.created_at,
                                    true,
                                  )
                                : "N/A"}
                          </TableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No recent activity
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              sx={{ p: 2, height: "100%", overflow: "hidden" }}
              elevation={3}
            >
              <Typography variant="h6" gutterBottom>
                System Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }} elevation={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Server Uptime
                    </Typography>
                    <Typography variant="h6">
                      {dashboardData.system?.server_uptime || "Unknown"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }} elevation={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Database Size
                    </Typography>
                    <Typography variant="h6">
                      {dashboardData.system?.database_size || "Unknown"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }} elevation={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Online Users
                    </Typography>
                    <Typography variant="h6">
                      {dashboardData.users?.active || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }} elevation={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Verified Users
                    </Typography>
                    <Typography variant="h6">
                      {dashboardData.users?.verified || 0}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

      </Box>
    </AdminLayout>
  );
};

export default Dashboard;
